/**
 * 大气散射片段着色器：单散射 + 二次散射近似 + 多次散射 LUT。
 * - Rayleigh 散射：λ⁻⁴ 系数（uBetaR）+ 相位函数 3/(16π)(1+cos²θ)
 * - Mie 散射：Henyey-Greenstein 相位函数 + 系数（uBetaM）
 * - 太阳透射率由 TransmittanceLUT 采样
 * - 多次散射：scatteringMode 可插拔（0=single, 1=secondOrder, 2=precomputedLUT）
 *   共享 GLSL 函数见 common.glsl.ts
 */

import { atmosphereCommonChunk } from './common.glsl'

export const atmosphereFragmentShader = /* glsl */ `
precision highp float;

uniform vec2 uResolution;
uniform vec3 uCamPos;      // km，相对地心
uniform vec3 uCamRight;
uniform vec3 uCamUp;
uniform vec3 uCamForward;
uniform vec2 uTanHalfFov;  // (tan(fov/2)*aspect, tan(fov/2))
uniform vec3 uSunDir;
uniform sampler2D uTransLUT;

uniform vec3 uBetaR;
uniform vec3 uBetaM;
uniform vec3 uBetaMExt;
uniform float uHr;
uniform float uHm;
uniform float uPlanetR;
uniform float uAtmosR;
uniform float uG;
uniform float uSunAngularRadius;
uniform float uSunIrradiance;
uniform vec3 uSkyAmbient;
uniform int uSampleSteps;
uniform int uScatteringMode; // 0=single, 1=secondOrder, 2=precomputedLUT
uniform float uMSIntensity;
uniform int uMSDirCount;     // 二次散射方向数（secondOrder）
uniform vec3 uMSDirs[16];    // 二次散射方向（黄金螺旋，CPU 生成）
uniform sampler2D uMSLUT;    // 多次散射 LUT（precomputedLUT）
uniform int uRayleighOnly;
uniform int uMieOnly;
uniform int uNoScatter;

varying vec2 vUv;

${atmosphereCommonChunk}

float hash3(vec3 p) {
  p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}

float noise3(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(hash3(i + vec3(0.0, 0.0, 0.0)), hash3(i + vec3(1.0, 0.0, 0.0)), f.x),
        mix(hash3(i + vec3(0.0, 1.0, 0.0)), hash3(i + vec3(1.0, 1.0, 0.0)), f.x), f.y),
    mix(mix(hash3(i + vec3(0.0, 0.0, 1.0)), hash3(i + vec3(1.0, 0.0, 1.0)), f.x),
        mix(hash3(i + vec3(0.0, 1.0, 1.0)), hash3(i + vec3(1.0, 1.0, 1.0)), f.x), f.y),
    f.z);
}

float fbm3(vec3 p) {
  float a = 0.5;
  float f = 0.0;
  for (int i = 0; i < 4; i++) {
    f += a * noise3(p);
    p *= 2.02;
    a *= 0.5;
  }
  return f;
}

// 程序化地表反照率（海洋/大陆 + 细分）
vec3 earthAlbedo(vec3 p) {
  p = normalize(p);
  float landMask = smoothstep(0.46, 0.54, fbm3(p * 2.5 + 1.7));
  float detail = clamp(fbm3(p * 6.0 + 3.0), 0.0, 1.0);
  vec3 ocean = vec3(0.03, 0.09, 0.16);
  vec3 landC = mix(vec3(0.16, 0.24, 0.10), vec3(0.38, 0.30, 0.16), detail);
  return mix(ocean, landC, landMask);
}

// 二次散射近似：沿视线每个采样点累加"来自天空方向的首次散射光再散射向相机"的贡献。
// 首次散射用 (βs/βe)·(1−T) 近似积分，方向集由 CPU 黄金螺旋生成（uMSDirs）。
vec3 computeSecondOrder(vec3 ro, vec3 rd) {
  if (uMSDirCount <= 0) return vec3(0.0);
  vec2 seg = atmosphereSegment(ro, rd);
  if (seg.x < 0.0) return vec3(0.0);
  float entry = seg.x;
  float exit = seg.y;
  float ds = (exit - entry) / float(uSampleSteps);
  vec3 outScatter = vec3(0.0);
  vec3 tauView = vec3(0.0);
  for (int i = 0; i < 128; i++) {
    if (i >= uSampleSteps) break;
    float t = entry + (float(i) + 0.5) * ds;
    vec3 P = ro + rd * t;
    float r = length(P);
    float h = max(r - uPlanetR, 0.0);
    float rhoR = exp(-h / uHr);
    float rhoM = exp(-h / uHm);
    vec3 extinction = uBetaR * rhoR + uBetaMExt * rhoM;
    tauView += extinction * ds;

    vec3 dirP = P / max(r, 1e-6);

    // 低于地平线的方向被地面遮挡 → 无首次散射光到达 P
    float horizonCos = -sqrt(max(1.0 - (uPlanetR * uPlanetR) / (r * r), 0.0));
    // 散射/消光比（吸收修正）
    vec3 betaS = uBetaR * rhoR + uBetaM * rhoM;
    vec3 betaE = uBetaR * rhoR + uBetaMExt * rhoM;
    vec3 bsBe = betaS / max(betaE, vec3(1e-6));

    vec3 l2 = vec3(0.0);
    for (int j = 0; j < 16; j++) {
      if (j >= uMSDirCount) break;
      vec3 wi = uMSDirs[j];
      float muWiP = dot(dirP, wi);
      if (muWiP <= horizonCos) continue; // 被地面遮挡
      vec3 tauWi = texture2D(uTransLUT, lutUV(r, muWiP)).rgb;
      vec3 transWi = exp(-tauWi);
      // 沿 ωᵢ 路径采样 4 个代表点的太阳透射率均值：
      // 阴影区本地 sunTrans≈0，但远处（上方）空气层被照亮，需取路径平均以捕捉该贡献
      float disc = r * r * (muWiP * muWiP - 1.0) + uAtmosR * uAtmosR;
      float tTop = max(-r * muWiP + sqrt(max(disc, 0.0)), 0.0);
      vec3 sunTransAvg = vec3(0.0);
      for (int k = 0; k < 4; k++) {
        vec3 Q = P + wi * (tTop * (float(k) + 0.5) / 4.0);
        float rQ = length(Q);
        vec3 dirQ = Q / max(rQ, 1e-6);
        vec3 tauQ = texture2D(uTransLUT, lutUV(rQ, dot(dirQ, uSunDir))).rgb;
        sunTransAvg += exp(-tauQ);
      }
      sunTransAvg *= 0.25;
      // 首次散射光（从 ωᵢ 方向到达 P）：
      //   S1 ≈ Is·P(ωᵢ·sun)·(βs/βe)·(1−T(P→top,ωᵢ))·⟨T_sun 沿路径均值⟩
      vec3 s1 = sunTransAvg * bsBe * (1.0 - transWi) * totalPhase(dot(wi, uSunDir));
      // 在 P 处散射向相机（第二次散射，需乘当地散射系数 βs）
      l2 += betaS * s1 * totalPhase(dot(wi, rd));
    }
    // 均匀球面积分权重 4π/N，并沿视线衰减/积分
    outScatter += l2 * (4.0 * PI / float(uMSDirCount)) * exp(-tauView) * ds;
  }
  return outScatter * uSunIrradiance;
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec2 ndc = uv * 2.0 - 1.0;
  vec3 rd = normalize(
    uCamForward + uCamRight * (ndc.x * uTanHalfFov.x) + uCamUp * (ndc.y * uTanHalfFov.y)
  );

  // 大气区间与地面命中
  vec2 seg = atmosphereSegment(uCamPos, rd);
  vec2 ground = intersectSphere(uCamPos, rd, uPlanetR);
  bool hitsGround = ground.x > 0.0 && (seg.y < 0.0 || ground.x < seg.y);

  vec3 inScatter = vec3(0.0);
  vec3 tauView = vec3(0.0);

  if (seg.x >= 0.0) {
    // 单散射（共享函数，输出视线方向光学厚度）
    inScatter = computeSingleScatter(uCamPos, rd, uSunDir, tauView);

    if (uScatteringMode == 1) {
      // secondOrder：沿视线双重积分近似
      inScatter += computeSecondOrder(uCamPos, rd) * uMSIntensity;
    } else if (uScatteringMode == 2) {
      // precomputedLUT：采样多次散射 LUT（x = V·L, y = L·up）
      vec3 up = uCamPos / max(length(uCamPos), 1e-6);
      float cosTheta = dot(rd, uSunDir);
      float cosThetaSun = dot(up, uSunDir);
      vec3 ms = texture2D(
        uMSLUT,
        vec2(
          clamp(cosTheta * 0.5 + 0.5, 0.0, 1.0),
          clamp(cosThetaSun * 0.5 + 0.5, 0.0, 1.0)
        )
      ).rgb;
      inScatter += ms * uMSIntensity;
    }
  }

  // ---- 太阳圆面 ----
  if (dot(rd, uSunDir) > cos(uSunAngularRadius)) {
    float rCam = length(uCamPos);
    float muCam = dot(uCamPos / max(rCam, 1e-6), uSunDir);
    vec3 tauSunCam = texture2D(uTransLUT, lutUV(rCam, muCam)).rgb;
    vec3 transSunCam = exp(-tauSunCam);
    float sunSolidAngle = PI * uSunAngularRadius * uSunAngularRadius;
    inScatter += vec3(1.0) * (uSunIrradiance / sunSolidAngle) * transSunCam;
  }

  // ---- 与地表合成（解析计算地表颜色，避免近平面精度问题） ----
  vec3 color;
  if (hitsGround) {
    vec3 gPos = uCamPos + rd * ground.x;
    vec3 n = normalize(gPos);
    vec3 albedo = earthAlbedo(gPos);
    vec3 earthCol;
    if (uNoScatter == 1) {
      earthCol = albedo; // 无散射 baseline：仅看地表本身
    } else {
      earthCol = albedo * (max(dot(n, uSunDir), 0.0) * uSunIrradiance + uSkyAmbient);
    }
    vec3 transToGround = exp(-tauView);
    color = earthCol * transToGround + inScatter;
  } else {
    color = inScatter;
  }

  gl_FragColor = vec4(color, 1.0);
}
`
